<template>
  <div class="main-panel">
    <!-- Keep existing navbar exactly as-is -->
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" />
    </div>

    <!-- Header / Title -->
    <header class="header">
      <div class="header__left">
        <div class="avatar" aria-hidden="true">📊</div>
        <div class="greet">
          <h1>Catálogo de reportes</h1>
          <p class="sub">Selecciona el reporte deseado</p>
        </div>
      </div>
      <div class="header__right">
        <button class="btn btn--ghost" @click="refresh" :disabled="loading" :aria-busy="loading">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 8 8h-2a6 6 0 1 1-6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35Z" fill="currentColor"/></svg>
          Actualizar
        </button>
        <button class="btn btn--danger" @click="logout">Cerrar sesión</button>
      </div>
    </header>

    <!-- Error banner -->
    <div v-if="error" class="error-banner" role="alert">
      {{ error }} <button class="btn btn--ghost" @click="refresh">Reintentar</button>
    </div>

    <!-- Grid of reports -->
    <section class="grid" aria-live="polite">
      <template v-if="!loading && filteredReports.length">
        <article
          v-for="(r, idx) in filteredReports"
          :key="r.key"
          class="card"
          role="link"
          tabindex="0"
          @click="go(r.to)"
          @keyup.enter="go(r.to)"
          :aria-label="`Abrir reporte: ${r.title}`"
        >
          <div class="card__media icon" aria-hidden="true">{{ r.icon }}</div>
          <div class="card__content">
            <div class="card__title">
              <h2>{{ r.title }}</h2>
              <span class="chip">#{{ idx + 1 }}</span>
            </div>
            <ul class="card__list">
              <li v-for="(p,i) in r.points" :key="i">{{ p }}</li>
            </ul>
            <div class="card__actions">
              <router-link class="btn btn--primary" :to="r.to">Entrar</router-link>
            </div>
          </div>
        </article>
      </template>

      <template v-else-if="loading">
        <div v-for="n in 6" :key="n" class="skeleton"></div>
      </template>

      <template v-else>
        <div class="empty">
          <svg viewBox="0 0 24 24" width="40" height="40" aria-hidden="true"><path d="M19 3H5a2 2 0 0 0-2 2v14l4-4h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Z" fill="currentColor"/></svg>
          <h3>No tienes reportes disponibles</h3>
          <p>Solicita acceso al administrador.</p>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ReportsCatalog',
  data() {
    return {
      username: '',
      firstName: '',
      lastName: '',
      groups: [],
      permissions: [],
      loading: true,
      error: null,
      reports: [
        {
          key: 'papeleta',
          title: 'Papeleta',
          to: { path: '/reporte-papeleta' },
          icon: '🧾',
          reqPerm: 'ui_access_report_papeleta',
        },
        {
          key: 'ot',
          title: 'Órdenes de trabajo',
          to: { path: '/reporte-ordenes-trabajo' },
          icon: '📋',
          reqPerm: 'ui_access_report_ordenes_trabajo',
        },
        {
          key: 'pend-fact',
          title: 'Pendientes por facturar',
          to: { path: '/reporte-pendientes-facturar' },
          icon: '⏳',
          reqPerm: 'ui_access_report_pendientes_facturar',
        },
        {
          key: 'mov-cartera',
          title: 'Movimientos de cartera',
          to: { path: '/reporte-movimientos-cartera' },
          icon: '💳',
          reqPerm: 'ui_access_report_movimientos_cartera',
        },
        {
          key: 'antig-saldos',
          title: 'Antigüedad de saldos',
          to: { path: '/reporte-antiguedad-saldos' },
          icon: '📈',
          reqPerm: 'ui_access_report_antiguedad_saldos',
        },
        {
          key: 'entradas-lote',
          title: 'Entradas a Lote',
          to: { path: '/reporte-entradas-lote' },
          icon: '📈',
          reqPerm: 'ui_access_report_entradas_lote',
        },
        {
          key: 'salidas-lote',
          title: 'Salidas a Lote',
          to: { path: '/reporte-salidas-lote' },
          icon: '📈',
          reqPerm: 'ui_access_report_salidas_lote',
        },
        {
          key: 'comsiones-operador',
          title: 'Comisiones de Operador',
          to: { path: '/reporte-comsiones-operador' },
          icon: '📈',
          reqPerm: 'rrhh.report_operator_commission',
        },
        {
          key: 'papeleta-cliente',
          title: 'Papeleta por Cliente',
          to: { path: '/reporte-papeleta-cliente' },
          icon: '🧾',
          reqPerm: 'ui_access_report_papeleta_cliente',
        },
        {
          key: 'corte-lote',
          title: 'Corte de Lote',
          to: { path: '/reporte-corte-lote' },
          icon: '📈',
          reqPerm: 'ui_access_report_corte_lote',
        },
        {
          key: 'venta-diaria',
          title: 'Venta Diaria',
          to: { path: '/reporte-venta-diaria' },
          icon: '📈',
          reqPerm: 'ui_access_report_venta_diaria',
        },
        {
          key: 'estadistico-unidades',
          title: 'Estadístico de Unidades',
          to: { path: '/reporte-estadistico-unidades' },
          icon: '📈',
          reqPerm: 'ui_access_report_estadistico_unidades',
        },
        {
          key: 'mantenimiento-unidades',
          title: 'Reporte de mantenimiento de unidades',
          to: { path: '/reporte-mantenimiento-unidades' },
          icon: '🔧',
          reqPerm: 'ui_access_report_mantenimiento_unidades',
        },
        {
          key: 'reporte-estadistico-clientes',
          title: 'Estadístico de Clientes',
          to: { path: '/reporte-estadistico-clientes' },
          icon: '📈',
          reqPerm: 'ui_access_report_estadistico_clientes',
        },
      ],
    }
  },
  computed: {
    filteredReports() {
    if (!this.permissions || !this.permissions.length) return []
    const userPerms = new Set(this.permissions || [])
    const hasPerm = (perm) => {
      if (!perm) return false
      if (userPerms.has(perm)) return true
      return userPerms.has(`access_control.${perm}`)
    }

    const hasAccess = (r) => {
      const reqPerm = r.reqPerm || null
      if (!reqPerm) return true
      return hasPerm(reqPerm)
    }

    return this.reports.filter(hasAccess)
  },
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.username = data.username
        this.firstName = data.first_name
        this.lastName = data.last_name
        this.groups = data.groups || []
        this.permissions = data.permissions || []
        if (
          !this.permissions.includes('ui_access_reports_catalogue') &&
          !this.permissions.includes('access_control.ui_access_reports_catalogue')
        ) {
          this.$router.push({ name: 'MainPanel' })
        }
      } catch (err) {
        console.error('Error fetching user:', err)
        this.error = 'No se pudo cargar la información del usuario.'
      } finally {
        this.loading = false
      }
    },
    
    refresh() { this.fetchCurrentUser() },
    logout() {
      // Also flush the Django session cookie (OAuth consent flow).
      axios.post(`${process.env.API_DIRECTORY}/logout/`).catch(() => {})
      this.$store.commit('setToken', null)
      localStorage.removeItem('authToken')
      this.$router.push({ name: 'UserLogin' })
    },
    go(to) { this.$router.push(to) },
  },
}
</script>

<style scoped>
/* Local theme tokens */
.main-panel {
  --bg: #ffffff;
  --panel: #151924;
  --muted: #373838;
  --text: #000000;
  --brand: #ffc434;
  --brand-ink: #3a2a00;
  --primary: #2563eb;
  --danger: #ef4444;
  --ring: rgba(37, 99, 235, 0.35);
}
@media (prefers-color-scheme: light) {
  .main-panel {
    --bg: #f7f8fb;
    --panel: #ffffff;
    --muted: #667085;
    --text: #0f172a;
    --brand: #ffc434;
    --brand-ink: #3a2a00;
    --primary: #2563eb;
    --danger: #dc2626;
    --ring: rgba(37, 99, 235, 0.25);
  }
}

.main-panel { min-height: 100vh; background: var(--bg); padding: 12vh 24px 48px; }

/* Keep navbar style */
.nav-bar-operations { position: absolute; top: 0; left: 0; width: 100vw; height: 10vh; background-color: var(--brand); z-index: 1000; display: flex; align-items: center; padding-left: 16px; box-shadow: 0 2px 0 rgba(0,0,0,.05); }
.logo-button { height: 55%; object-fit: contain; }

/* Header */
.header { margin-top: 12px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.header__left { display: flex; align-items: center; gap: 14px; }
.avatar { width: 44px; height: 44px; border-radius: 10px; display: grid; place-items: center; background: linear-gradient(135deg, var(--brand), #ffe188); color: var(--brand-ink); font-weight: 800; box-shadow: 0 6px 18px rgba(0,0,0,.15); }
.greet h1 { font-size: 1.15rem; color: var(--text); margin: 0; }
.greet .sub { margin: 2px 0 0; color: var(--muted); font-size: .9rem; }
.header__right { display: flex; gap: 10px; }

/* Error banner */
.error-banner { margin: 12px 0; padding: 10px 12px; border-radius: 10px; border:1px solid rgba(239,68,68,.35); background: rgba(239,68,68,.08); color: var(--danger); }

/* Grid */
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 18px; margin-top: 10px; }
.card { background: #fff; border: 1px solid rgba(0,0,0,.08); border-radius: 18px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,.08); transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease; outline: none; cursor: pointer; }
.card:focus-visible { box-shadow: 0 0 0 6px var(--ring); }
.card:hover { transform: translateY(-3px); border-color: rgba(0,0,0,.12); }
.card:active { transform: translateY(-1px) scale(0.99); }

.card__media.icon { height: 140px; display: grid; place-items: center; font-size: 56px; }
.card__content { padding: 14px; display: grid; gap: 10px; }
.card__title { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.card__title h2 { font-size: 1rem; margin: 0; color: var(--text); }
.chip { font-size: .7rem; background: rgba(37,99,235,.15); color: #2563eb; padding: 4px 8px; border-radius: 999px; border: 1px solid rgba(37,99,235,.35); }
.card__list { margin: 0; padding-left: 16px; color: var(--muted); }
.card__list li { margin: 3px 0; }
.card__actions { display: flex; justify-content: flex-end; }

/* Buttons */
.btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 12px; border: 1px solid transparent; background: var(--panel); color: var(--text); cursor: pointer; text-decoration: none; transition: transform .15s ease, box-shadow .2s ease, background .2s ease, color .2s ease; }
.btn:focus { outline: none; box-shadow: 0 0 0 6px var(--ring); }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.btn--primary { background: var(--primary); color: white; }
.btn--primary:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(37,99,235,.35); }
.btn--danger { background: var(--danger); color: white; }
.btn--danger:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(239,68,68,.3); }
.btn--ghost { background: transparent; border: 1px solid rgba(0,0,0,.15); color: var(--text); }
.btn--ghost:hover { background: rgba(0,0,0,.06); }

/* Skeletons */
.skeleton { height: 220px; border-radius: 18px; background: linear-gradient(90deg, rgba(0,0,0,.03), rgba(0,0,0,.06), rgba(0,0,0,.03)); background-size: 200% 100%; animation: shimmer 1.2s infinite linear; border: 1px solid rgba(0,0,0,.06); }
@keyframes shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

/* Empty */
.empty { text-align: center; color: var(--muted); padding: 36px 0; }
.empty h3 { margin: 8px 0 4px; color: var(--text); }

/* Responsiveness */
@media (max-width: 720px) { .header { flex-direction: column; align-items: stretch; gap: 12px; } .header__right { justify-content: space-between; } }
@media screen and (max-height: 586px) { .nav-bar-operations { height: 10vh; } .main-panel { padding-top: 12vh; } }
@media screen and (min-height: 930px) { .main-panel { padding-top: 14vh; } }

/* Reduced motion */
@media (prefers-reduced-motion: reduce) { .card, .btn, .skeleton { transition: none; animation: none; } }
</style>
